import { ApiError } from '../services/api.types'
import type { TFunction } from '../hooks/useTranslationSafe'

/**
 * Turns a fetch failure into a message an end user can actually act on, instead of
 * a raw "API Error: 403 Forbidden" (or whatever the network layer happened to throw).
 * Only `ApiError` (thrown by the kit's own `createFetcher`) carries a real HTTP status
 * to branch on — a custom `fetcher` can throw anything, so unrecognized shapes still
 * fall back to the original generic `error` message (interpolating `.message`) rather
 * than guessing.
 */
export function getFriendlyErrorMessage(error: Error, t: TFunction): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return t('error.unauthorized')
      case 403:
        return t('error.forbidden')
      case 404:
        return t('error.notFound')
      case 408:
        return t('error.timeout')
      case 429:
        return t('error.rateLimited')
      default:
        if (error.status >= 500) return t('error.server')
        break
    }
  }

  // A custom fetcher's own network failure (fetch()/axios rejecting before a response
  // ever comes back — offline, DNS failure, CORS, connection reset) has no HTTP status
  // at all; browsers surface it as a TypeError with this wording in both Chromium and
  // Firefox (axios instead sets `.message === 'Network Error'`).
  if (error instanceof TypeError && /fetch|network/i.test(error.message)) {
    return t('error.network')
  }
  if (error.message === 'Network Error') {
    return t('error.network')
  }

  return t('error', { message: error.message })
}
